import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { SEO } from "../../../components/SEO";
import {
  DetailRoot,
  DetailHeader,
  DetailTitle,
  DetailContent,
  DetailInfo,
  DetailGallery,
  GalleryImage,
  DetailBanner,
  DetailContainer,
  TitleInfo,
  InfoList,
  InfoItem,
  InfoLabel,
  InfoValue,
  Description,
  TagList,
  Tag,
  DescriptionText,
  DescriptionContainer,
  QuoteBlock,
  RegularText,
  SeeMoreWorks,
  SeeMoreContainer,
  SeeMoreHeader,
  SeeMoreTitle,
  ViewMoreButton,
  SeeMoreGrid,
  WorkCard,
  WorkCardImage,
  WorkCardTitle,
  WorkCardTags,
  WorkCardTag,
  VideoSection,
  VideoWrapper,
  MobileTopNav,
  BackButton,
  MobileTitle,
  PlaceholderIcon,
  WorkCardContent,
  WorkCardImageContainer
} from "./styles";
import { useHeader } from '../../../contexts/HeaderContext';
import { fetchHomeWorks, fetchWorkById, fetchWorks, WorkDetailItem, WorkItem } from "apis/works";
import { trackVisit } from "../../../apis/analytics";

const WorkDetail = () => {
  const { urlPath } = useParams<{ urlPath: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [work, setWork] = useState<WorkDetailItem | null>();

  // Description을 파싱하여 인용구와 일반 텍스트를 분리하는 함수
  const parseDescription = (description: string) => {
    // 인용구 패턴을 찾기 위한 정규식들
    const quotePatterns = [
      // "텍스트" 형태의 인용구
      /"([^"]+)"/g,
      // 「텍스트」 형태의 인용구
      /「([^」]+)」/g,
      // 『텍스트』 형태의 인용구
      /『([^』]+)』/g,
      // <blockquote> 태그
      /<blockquote[^>]*>(.*?)<\/blockquote>/gi,
      // > 로 시작하는 인용구 (마크다운 스타일)
      /^>\s*(.+)$/gm
    ];

    let parts: Array<{ type: 'quote' | 'text', content: string }> = [];
    let lastIndex = 0;
    let foundQuotes: Array<{ start: number, end: number, content: string }> = [];

    // 모든 인용구 패턴을 찾아서 저장
    quotePatterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(description)) !== null) {
        foundQuotes.push({
          start: match.index,
          end: match.index + match[0].length,
          content: match[1] || match[0]
        });
      }
    });

    // 인용구를 시작 위치 순으로 정렬
    foundQuotes.sort((a, b) => a.start - b.start);

    // 겹치는 인용구 제거
    const uniqueQuotes = foundQuotes.filter((quote, index) => {
      if (index === 0) return true;
      const prevQuote = foundQuotes[index - 1];
      return quote.start >= prevQuote.end;
    });

    // 텍스트를 인용구와 일반 텍스트로 분리
    uniqueQuotes.forEach(quote => {
      // 인용구 이전의 일반 텍스트 추가
      if (lastIndex < quote.start) {
        const textContent = description.slice(lastIndex, quote.start).trim();
        if (textContent) {
          parts.push({ type: 'text', content: textContent });
        }
      }
      
      // 인용구 추가
      parts.push({ type: 'quote', content: quote.content.trim() });
      lastIndex = quote.end;
    });

    // 마지막 인용구 이후의 일반 텍스트 추가
    if (lastIndex < description.length) {
      const textContent = description.slice(lastIndex).trim();
      if (textContent) {
        parts.push({ type: 'text', content: textContent });
      }
    }

    // 인용구가 없으면 전체를 일반 텍스트로 처리
    if (parts.length === 0) {
      parts.push({ type: 'text', content: description });
    }

    return parts;
  };

  useEffect(() => {
    const loadWorks = async () => {
      try {
        const workData = await fetchWorkById(String(urlPath));
        setWork(workData); // Add type assertion to fix type mismatch
      } catch (error) {
        console.error('Failed to fetch works:', error);
      }
    };
    loadWorks();
  }, [urlPath]);

  // work 데이터가 로드된 후 정확한 workId로 visitLog 전송
  useEffect(() => {
    if (work && work.id) {
      console.log(`[WorkDetail] Tracking visit with workId: ${work.id}`);
      trackVisit(location.pathname, work.id);
    }
  }, [work, location.pathname]);

  const [recentWorks, setRecentWorks] = useState<WorkItem[]>([]);
  const { setIsTransparent } = useHeader();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // 페이지 로드 시 스크롤 위치를 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  // 모바일 여부 체크를 위한 useEffect 추가
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 헤더 투명도 설정 useEffect 수정
  useEffect(() => {
    if (!isMobile) {  // 모바일이 아닐 때만 투명 헤더 적용
      setIsTransparent(true);
      return () => {
        setIsTransparent(false);
      };
    } else {
      setIsTransparent(false);  // 모바일에서는 투명 헤더 비활성화
    }
    return () => {
      setIsTransparent(false);
    };
  }, [setIsTransparent, isMobile]);

  useEffect(() => {
    const loadRecentWorks = async () => {
      try {
        const recentWorksData = await fetchWorks();
        const filteredWorks = recentWorksData
          .filter(w => w.id !== Number(urlPath))
          .sort((a, b) => b.id - a.id)
          .slice(0, 6);

        setRecentWorks(filteredWorks);
      } catch (error) {
        console.error('Failed to fetch recent works:', error);
      }
    };
    loadRecentWorks();
  }, [urlPath, navigate, work]);
  
  // 페이지 이동 핸들러 함수 수정
  const handleNavigation = (path: string) => {
    if (path.startsWith('/works/')) {
      // 다른 작품 디테일 페이지로 이동할 때는 현재 상태 유지
      navigate(path);
      window.scrollTo(0, 0);
    } else if (path === '/works') {
      navigate(path);
      window.scrollTo(0, 0);
    } else {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };
  
  // 뒤로가기 핸들러 함수 수정
  const handleGoBack = () => {
    // 세션 스토리지의 visibleCount는 이미 저장되어 있으므로 
    // 브라우저의 뒤로가기 기능만 실행
    navigate(-1);
  };
  
  if (!work) {
    return <div>Loading...</div>;
  }
  
  return (
    <DetailRoot>
      <SEO 
        title={`${work.title} - 퍼즐코퍼레이션`}
        description={work.metaDescription}
        keywords={work.metaKeywords}
        ogTitle={`${work.title} - 퍼즐코퍼레이션`}
        ogDescription={work.metaDescription}
        ogImage={work.thumbnailUrl}
      />
      <MobileTopNav>
        <BackButton onClick={handleGoBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="#1D1D1D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </BackButton>
        <MobileTitle>{work.title}</MobileTitle>
        <PlaceholderIcon />
      </MobileTopNav>
      
      <DetailBanner>
        <img src={work.thumbnailUrl} alt={work.title} />
      </DetailBanner>
      
      <DetailContainer>
        <DetailHeader>
        </DetailHeader>
        
        <DetailContent>
          <DetailInfo>
            <TitleInfo>
              <DetailTitle>{work.title}</DetailTitle>
              <InfoList>
                <InfoItem>
                  <InfoLabel>Client</InfoLabel>
                  <InfoValue>{work.client}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Brand</InfoLabel>
                  <InfoValue>{work.brand}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Industry</InfoLabel>
                  <InfoValue>{work.industry}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Work Scope</InfoLabel>
                  <InfoValue>{work.workScope}</InfoValue>
                </InfoItem>
              </InfoList>
            </TitleInfo>
            
            <Description>
              <TagList>
                {work.tags.map((tag, index) => (
                  <Tag key={index}>{tag}</Tag>
                ))}
              </TagList>
              <DescriptionContainer>
                {parseDescription(work.description).map((part, index) => {
                  if (part.type === 'quote') {
                    return (
                      <QuoteBlock key={index}>
                        <div dangerouslySetInnerHTML={{ __html: part.content }} />
                      </QuoteBlock>
                    );
                  } else {
                    return (
                      <RegularText 
                        key={index}
                        dangerouslySetInnerHTML={{ __html: part.content }}
                      />
                    );
                  }
                })}
              </DescriptionContainer>
            </Description>
          </DetailInfo>
        </DetailContent>
        
        {work.detailImageUrls && work.detailImageUrls.length > 0 && (
          <DetailGallery>
            {work.detailImageUrls.map((image, index) => (
              <GalleryImage key={index}>
                <img src={image} alt={`Gallery ${index + 1}`} />
              </GalleryImage>
            ))}
          </DetailGallery>
        )}
        
        {work.videos && work.videos.length > 0 && (
          <VideoSection>
            {work.videos.slice(0, 5).map((videoUrl, index) => (
              <VideoWrapper key={index}>
                <iframe
                  src={videoUrl}
                  title={`Video ${index + 1}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </VideoWrapper>
            ))}
          </VideoSection>
        )}
      </DetailContainer>
      
      <SeeMoreWorks>
        <SeeMoreContainer>
          <SeeMoreHeader>
            <SeeMoreTitle>You might also like</SeeMoreTitle>
            {!isMobile && (  // PC에서만 여기에 표시
              <ViewMoreButton onClick={() => handleNavigation('/works')}>
                View More
              </ViewMoreButton>
            )}
          </SeeMoreHeader>
          
          <SeeMoreGrid>
            {recentWorks.map(recentWork => (
              <WorkCard 
                key={recentWork.id} 
                onClick={() => handleNavigation(`/works/${recentWork.slug}`)}
              >
                <WorkCardImageContainer>
                  <WorkCardImage src={recentWork.thumbnailUrl} alt={recentWork.title} />
                </WorkCardImageContainer>
                <WorkCardContent>
                  <WorkCardTitle>{recentWork.title}</WorkCardTitle>
                  <WorkCardTags>
                    {recentWork.workScope && <WorkCardTag>#{recentWork.workScope}</WorkCardTag>}
                    {recentWork.industry && <WorkCardTag>#{recentWork.industry}</WorkCardTag>}
                  </WorkCardTags>
                </WorkCardContent>
              </WorkCard>
            ))}
          </SeeMoreGrid>
          
          {isMobile && (  // 모바일에서만 여기에 표시
            <ViewMoreButton onClick={() => handleNavigation('/works')}>
              View More
            </ViewMoreButton>
          )}
        </SeeMoreContainer>
      </SeeMoreWorks>
    </DetailRoot>
  );
};

export default WorkDetail; 